import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Info, X } from "lucide-react";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

function safeLink(value: string) {
  try { const url = new URL(value); return ["https:", "http:"].includes(url.protocol) ? url : null; } catch { return null; }
}

/** Local source metadata; no remote screenshot service or background requests. */
export function LinkPreview({ url, title, children }: { url: string; title: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 0, top: 0, night: false });
  const trigger = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const card = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const id = useId();
  const reduced = useReducedMotion();
  const parsed = safeLink(url);
  const keepOpen = () => clearTimeout(closeTimer.current);
  const show = () => {
    keepOpen();
    const rect = trigger.current?.getBoundingClientRect();
    if (!rect) return;
    setPosition({ left: Math.max(12, Math.min(rect.left, window.innerWidth - 332)), top: Math.max(12, Math.min(rect.bottom + 8, window.innerHeight - 240)), night: Boolean(trigger.current?.closest(".night-paper")) });
    setOpen(true);
  };
  const leave = () => { closeTimer.current = setTimeout(() => { if (!trigger.current?.contains(document.activeElement) && !card.current?.contains(document.activeElement)) setOpen(false); }, 180); };
  useEffect(() => () => clearTimeout(closeTimer.current), []);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const outside = (event: Event) => { if (!trigger.current?.contains(event.target as Node) && !card.current?.contains(event.target as Node)) close(); };
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") { close(); if (card.current?.contains(document.activeElement)) button.current?.focus(); } };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("focusin", outside);
    document.addEventListener("keydown", escape);
    window.addEventListener("resize", close);
    const onScroll = (event: Event) => { if (!card.current?.contains(event.target as Node)) close(); };
    window.addEventListener("scroll", onScroll, true);
    return () => { document.removeEventListener("pointerdown", outside); document.removeEventListener("focusin", outside); document.removeEventListener("keydown", escape); window.removeEventListener("resize", close); window.removeEventListener("scroll", onScroll, true); };
  }, [open]);
  if (!parsed) return <>{children}</>;
  return <><span className="source-link" ref={trigger} onPointerEnter={event => { if (event.pointerType === "mouse") show(); }} onPointerLeave={leave}>
    <a href={parsed.href} target="_blank" rel="noopener noreferrer">{children}</a>
    <button ref={button} type="button" className="source-preview-trigger" aria-label={`Об источнике: ${title}`} aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => { show(); requestAnimationFrame(() => card.current?.querySelector("a")?.focus()); }}><Info size={14} /></button>
  </span>{createPortal(<AnimatePresence>{open && <motion.div ref={card} id={id} role="region" aria-label="Превью источника" className={`source-preview ${position.night ? "source-preview-night" : ""}`} style={{ left: position.left, top: position.top }} onPointerEnter={keepOpen} onPointerLeave={leave}
    initial={reduced ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: reduced ? 0 : .15 }}>
    <span className="source-preview-heading">Источник<button type="button" aria-label="Закрыть превью" onClick={() => { setOpen(false); button.current?.focus(); }}><X size={16} /></button></span>
    <strong>{title || parsed.hostname}</strong><small>{parsed.hostname.replace(/^www\./, "")}</small>
    <a href={parsed.href} target="_blank" rel="noopener noreferrer">Открыть источник <ArrowUpRight size={16} /></a>
  </motion.div>}</AnimatePresence>, document.body)}</>;
}
